// LoansStep — the Loans step of the payroll wizard, between Deductions and Adjustments (PRD_LoansManagement C2 / C13 / C14 / C15).
//
// One row per (employee, qualifying loan); one input per period month with a
// `Due this month:` chip. Copies DeductionsStep's chip markup and classes (the
// pw-table--deductions descendant selectors) — it does NOT share a component
// (C1: Step 4 is untouched). Every keystroke lifts into payrollRun.employees[]
// .deductions.loans through the parent (onLoanUpdate), exactly as Step 4's
// government maps do, so the net check, the Adjustments and Summary columns and the submit
// totals read it live.
//
// On mount: fetch the company's non-voided loans (C13, one single-field query)
// and reconcile the run's existing entries — drop a loan that no longer
// qualifies, clamp an amount above the LOAN's remaining balance, both with a
// pinned notice. Nothing is pre-filled: C2 withdrew the pre-fill at /test-impl,
// so every input starts empty and the chip only says what is due.

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { format } from 'date-fns';
import { parsePeriodToDate, toYMDString, getMonthsInRange } from '../../utils/periodDate';
import { formatMonthLabel } from '../../utils/payslipBuilders';
import { calculateEmployeeFinancials } from '../../utils/payrollCalculations';
import { formatPeso as peso } from '../../utils/currency';
import { fetchCompanyLoans } from '../../services/loanService';
import {
  qualifies,
  monthRemaining,
  loanRemaining,
  num,
} from '../../utils/loanMath';
import { round2 } from '../../services/contributionTableClient';
import { matchesEmployeeSearch } from '../../utils/employeeSearch';

// ── C14 — pinned strings ─────────────────────────────────────────────────────
// (t) — word for word Step 4's pair, so the two steps read the same.
// (a) — "Bal:" until /test-impl 2026-09-26, when the input's ceiling moved from the
// month to the loan and the two figures stopped being the same number. A chip reading
// "Bal: ₱0.00" beside a box that accepts ₱300 reads as broken; this says what the
// figure actually is. Deliberately NOT Step 4's wording, because Step 4's chip IS its cap.
export const DUE_LABEL = 'Due this month';
export const APPLY_ALL_ACTION = 'Apply to All';
export const CLEAR_ALL_ACTION = 'Clear All';
export const EMPTY_STATE = 'No active loans for the employees in this run.'; // (b)
export const FETCH_ERROR = "Couldn't load loans. Retry, or go back and try again."; // (k)
// (l) — removal, singular / plural, and the clamp variant.
export const removedNotice = (names) =>
  names.length === 1
    ? `1 loan was removed from this run because it is no longer being deducted: ${names[0]}.`
    : `${names.length} loans were removed from this run because they are no longer being deducted: ${names.join(', ')}.`;
// C14(l) — "on the loan", not "for the month": C2 moved the ceiling to the loan's
// own balance, and the two figures now differ by an order of magnitude
// (/impl-review r2, 2026-09-27).
export const clampedNotice = (names) =>
  names.length === 1
    ? `1 loan amount was reduced to what is left on the loan: ${names[0]}.`
    : `${names.length} loan amounts were reduced to what is left on the loan: ${names.join(', ')}.`;
// C15 — the same wording as the Next tooltip; the block itself stays at Submit.
export const negativeNetLine = (n) =>
  `${n} employee${n === 1 ? ' has' : 's have'} a negative net pay. Reduce their deductions, loans or adjustments so net pay is at least ₱0.`;

const empKey = (emp) => emp.employeeId || emp.id;
const getFullName = (employee) => {
  const firstName = employee.personalInfo?.firstName || '';
  const lastName = employee.personalInfo?.lastName || '';
  if (lastName && firstName) return `${lastName}, ${firstName}`;
  return employee.personalInfo?.fullName || lastName || firstName || 'Unknown';
};
const getInitials = (employee) => {
  const first = employee.personalInfo?.firstName?.charAt(0) || '';
  const last = employee.personalInfo?.lastName?.charAt(0) || '';
  return `${first}${last}`.toUpperCase() || '??';
};
const getDepartment = (employee) => employee.employment?.department || employee.department || '';

const sumOthers = (entries, loanId, monthKey) =>
  entries
    .filter((e) => e.loanId === loanId && e.monthKey !== monthKey)
    .reduce((s, e) => s + num(e.amount), 0);

// The most this cell may hold: what is LEFT OF THE LOAN, less what the other
// month columns already carry. Deliberately NOT capped at the month's
// amortization — an employee may settle a cash advance in one go, or choose to
// overpay a government loan (founder, /test-impl 2026-09-26). The ceiling that
// matters is the debt itself: you can never collect more than is owed.
const capFor = (loan, monthKey, entries) =>
  Math.max(0, round2(loanRemaining(loan) - sumOthers(entries, loan.id, monthKey)));

const entryOf = (loan, monthKey, amount) => ({
  loanId: loan.id,
  name: loan.name,
  monthKey,
  amount: round2(num(amount)),
});

/**
 * Pure reconcile + pre-fill for one run line (C13, C2). Exported for the test.
 * @returns {{ entries: Array, changed: boolean, removed: string[], clamped: string[] }}
 */
export const reconcileLine = (emp, loans, monthKeys) => {
  const id = String(empKey(emp));
  const mine = loans.filter((l) => String(l.employeeId) === id && qualifies(l, monthKeys));
  const byId = new Map(mine.map((l) => [l.id, l]));
  const existing = Array.isArray(emp.deductions?.loans) ? emp.deductions.loans : [];
  const removed = new Map();
  const clamped = new Map();
  let changed = false;

  // 1. Drop entries whose loan no longer qualifies (or whose month left the period).
  let entries = existing.filter((e) => {
    const loan = byId.get(e.loanId);
    if (!loan) {
      removed.set(e.loanId, e.name || e.loanId);
      changed = true;
      return false;
    }
    if (!monthKeys.includes(e.monthKey)) {
      changed = true;
      return false;
    }
    return true;
  });

  // 2. Clamp anything above what the loan's own balance now allows (C2 moved
  //    the ceiling off the month; capFor carries no month term).
  // Each column is capped against the OTHER columns' pre-clamp amounts (`entries`
  // is read inside the map, before the reassignment). When two months' saved
  // amounts together exceed the loan's remainder, both are cut against the same
  // surplus and the run collects less than the loan allows — the loan takes one
  // more cutoff. Reachable only on a cross-month period with a hand-typed second
  // column and a run approved against the loan in between. Documented, not
  // handled (impl-review 2026-09-24): no money leaves wrongly.
  entries = entries.map((e) => {
    const loan = byId.get(e.loanId);
    const cap = capFor(loan, e.monthKey, entries);
    if (num(e.amount) > cap) {
      clamped.set(e.loanId, loan.name);
      changed = true;
      return { ...e, amount: cap };
    }
    return e;
  });

  // 3. NO pre-fill. The first build proposed half the monthly amortization per
  //    cutoff, on the premise that payroll is exactly two cutoffs a month — but
  //    the wizard accepts a CUSTOM date range, and a six-day run was still
  //    offered half a month (/test-impl 2026-09-26). A wrong number that is
  //    already in the box gets approved unnoticed; an empty box does not. The
  //    runner types the amount, or clicks the chip to take what is left of the
  //    month. Skipping the step now collects nothing, which is the safer failure
  //    and is visible on the Loans page as a balance that did not move.

  return { entries, changed, removed: [...removed.values()], clamped: [...clamped.values()] };
};

const LoansStep = ({
  employees,
  periodStart,
  periodEnd,
  companyId,
  onLoanUpdate,
  onLoansReplace,
  onBlockedChange,
}) => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notices, setNotices] = useState({ removed: [], clamped: [] });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const reconciledRef = useRef(false);
  const employeesRef = useRef(employees);
  employeesRef.current = employees;
  const replaceRef = useRef(onLoansReplace);
  replaceRef.current = onLoansReplace;
  const blockedRef = useRef(onBlockedChange);
  blockedRef.current = onBlockedChange;

  const startYMD = toYMDString(periodStart);
  const endYMD = toYMDString(periodEnd);
  const monthKeys = useMemo(
    () => (startYMD && endYMD ? getMonthsInRange(startYMD, endYMD) : []),
    [startYMD, endYMD],
  );
  const isCrossMonth = monthKeys.length > 1;

  // ── Fetch (C13) ────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      setLoans(await fetchCompanyLoans(companyId));
    } catch (err) {
      console.error('[LoansStep] loans fetch failed:', err);
      setError(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  // Next is held while loading or errored; released on unmount (C13).
  useEffect(() => {
    if (blockedRef.current) blockedRef.current(loading || Boolean(error));
  }, [loading, error]);
  useEffect(() => () => { if (blockedRef.current) blockedRef.current(false); }, []);

  // ── Reconcile + pre-fill, once per mount, after the fetch (C13, C2) ────────
  useEffect(() => {
    if (loading || error || reconciledRef.current) return;
    reconciledRef.current = true;
    const removed = [];
    const clamped = [];
    (employeesRef.current || []).forEach((emp) => {
      const result = reconcileLine(emp, loans, monthKeys);
      removed.push(...result.removed);
      clamped.push(...result.clamped);
      if (result.changed && replaceRef.current) replaceRef.current(empKey(emp), result.entries);
    });
    setNotices({ removed, clamped });
  }, [loading, error, loans, monthKeys]);

  // ── Derived view ───────────────────────────────────────────────────────────
  const rows = useMemo(() => {
    const sorted = [...(employees || [])].sort((a, b) => getFullName(a).localeCompare(getFullName(b)));
    return sorted
      .map((emp) => ({
        emp,
        loans: loans
          .filter((l) => String(l.employeeId) === String(empKey(emp)) && qualifies(l, monthKeys))
          .sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .filter((r) => r.loans.length > 0);
  }, [employees, loans, monthKeys]);

  const departments = useMemo(
    () => [...new Set(rows.map((r) => getDepartment(r.emp)))].filter(Boolean).sort(),
    [rows],
  );
  const displayedRows = useMemo(() => {
    return rows.filter(({ emp }) => {
      if (selectedDepartment !== 'all' && getDepartment(emp) !== selectedDepartment) return false;
      return matchesEmployeeSearch(emp, searchTerm);
    });
  }, [rows, searchTerm, selectedDepartment]);

  const entriesOf = (emp) => (Array.isArray(emp.deductions?.loans) ? emp.deductions.loans : []);
  const amountOf = (emp, loan, monthKey) =>
    num(entriesOf(emp).find((e) => e.loanId === loan.id && e.monthKey === monthKey)?.amount);

  const totalThisRun = useMemo(
    () => rows.reduce((s, { emp }) => s + entriesOf(emp).reduce((t, e) => t + num(e.amount), 0), 0),
    [rows],
  );
  const negativeNet = useMemo(
    () => (employees || []).filter((emp) => calculateEmployeeFinancials(emp).net < 0),
    [employees],
  );

  // ── Edits (lifted live, like handleDeductionUpdate) ────────────────────────
  const setAmount = (emp, loan, monthKey, raw) => {
    const parsed = parseFloat(raw);
    const wanted = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    const cap = capFor(loan, monthKey, entriesOf(emp));
    if (onLoanUpdate) onLoanUpdate(empKey(emp), entryOf(loan, monthKey, Math.min(wanted, cap)));
  };

  // Apply to All (C14 t) — ONE month per loan: the first period month that
  // actually OWES something.
  //
  // Not "every payable month" (the first build): on a cross-month period that
  // takes TWO amortizations out of one pay run. Not "the attribution month"
  // (the second): on a 26th-10th cutoff the earlier month is usually already
  // collected, so that fills 0 everywhere while the later column's chip shows
  // money due. Skipping a paid month is the whole difference - both failures
  // were found in consecutive review rounds (/impl-review r2, r3).
  //
  // It WRITES the entries rather than merging them, so a loan with nothing due
  // gets no entry at all. A zero entry is not free: it still carries the loanId
  // into deductions.loans, which loanIdsInPendingRuns reads - locking the loan
  // against a pending run that will credit it nothing (C16).
  //
  // monthRemaining is already capped at loanRemaining and only one month per
  // loan is ever filled, so the columns can never sum past the debt. No
  // separate cap term: subtracting the other columns is what under-filled the
  // second build, since they are being cleared in the same pass.
  //
  // Like Step 4 and Clear All, it acts on every row in the step, not only the
  // filtered ones.
  const applyToAll = () => {
    rows.forEach(({ emp, loans: mine }) => {
      const existing = entriesOf(emp);
      const entries = [];
      mine.forEach((loan) => {
        const k = monthKeys.find(
          (m) => m >= loan.firstDeductionMonth && monthRemaining(loan, m) > 0,
        );
        if (k) {
          entries.push(entryOf(loan, k, monthRemaining(loan, k)));
          return;
        }
        // Nothing due on this loan in this period, so the button has no opinion
        // about it — keep anything typed by hand instead of clearing it. The
        // chip deliberately still invites an entry at ₱0.00 due (C14 a), so
        // clearing it would silently drop a deliberate overpayment
        // (/impl-review, 2026-09-28). Zero entries are still never written.
        existing
          .filter((e) => e.loanId === loan.id && num(e.amount) > 0)
          .forEach((e) => entries.push(e));
      });
      if (onLoansReplace) onLoansReplace(empKey(emp), entries);
    });
  };

  // Walks `rows`, NOT the filtered set — Clear All means the step, not the
  // current view, exactly as applyToAll above and Step 4 do. Do not "tidy" this
  // to displayedRows (/impl-review r4).
  const clearAll = () => {
    rows.forEach(({ emp }) => {
      if (onLoansReplace) onLoansReplace(empKey(emp), []);
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const bandStyle = (kind) => ({
    padding: '12px 16px',
    marginBottom: '16px',
    borderRadius: 8,
    fontSize: 13,
    lineHeight: 1.5,
    backgroundColor: kind === 'danger' ? 'var(--ui-danger-tint)' : 'var(--ui-primary-tint)',
    border: `1px solid ${kind === 'danger' ? 'var(--ui-danger)' : 'var(--ui-primary)'}`,
    color: kind === 'danger' ? 'var(--ui-danger-text)' : 'var(--ui-primary-hover)',
  });

  // The notices ride above whichever body renders — a reconcile that removed the last
  // loan still has to say so over the empty state (C13/C14l).
  const notices_ = (
    <>
      {notices.removed.length > 0 && <div style={bandStyle('info')} role="status">{removedNotice(notices.removed)}</div>}
      {notices.clamped.length > 0 && <div style={bandStyle('info')} role="status">{clampedNotice(notices.clamped)}</div>}
      {negativeNet.length > 0 && (
        <div style={bandStyle('danger')} role="alert">
          {negativeNetLine(negativeNet.length)}
          {' '}Affected: {negativeNet.map((e) => e.personalInfo?.fullName || getFullName(e)).join(', ')}.
        </div>
      )}
    </>
  );

  if (loading) {
    return (
      <div className="deductions-step">
        <div className="deductions-empty"><p>Loading loans…</p></div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="deductions-step">
        <div className="deductions-empty">
          <p>{error}</p>
          <button type="button" className="btn btn-secondary" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="deductions-step">
        {notices_}
        <div className="deductions-empty"><p>{EMPTY_STATE}</p></div>
      </div>
    );
  }

  return (
    <div className="deductions-step">
      {notices_}

      <div className="deductions-content">
        <div className="content-header">
          <div className="pw-header">
            <h2 className="pw-header__title">Loan Deductions</h2>
            <span className="pw-header__badge">
              {format(parsePeriodToDate(periodStart), 'MMM d, yyyy')} – {format(parsePeriodToDate(periodEnd), 'MMM d, yyyy')}
            </span>
          </div>
          {isCrossMonth && (
            <span className="cross-month-badge">
              Cross-month period: one column per month — each loan is payable from the first month on or after its first deduction month.
            </span>
          )}
        </div>

        <div className="pw-toolbar">
          <div className="pw-toolbar__controls">
            <input
              type="text"
              className="pw-toolbar__search"
              placeholder="Search by name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="pw-toolbar__select"
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              aria-label="Department"
            >
              <option value="all">All Departments</option>
              {departments.map((dept) => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
          <div className="bulk-selection__actions" style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" className="btn btn-primary" onClick={applyToAll}>
              {APPLY_ALL_ACTION}
            </button>
            <button type="button" className="btn btn-secondary" onClick={clearAll}>
              {CLEAR_ALL_ACTION}
            </button>
          </div>
        </div>

        {/* C2/C14(a) — the chip says what THIS MONTH owes; the box accepts up to
            the whole loan. That difference lives nowhere else on screen, and a
            hover title never appears on a tablet (/impl-review r2). */}
        <p className="pw-step-note">
          The chip shows what each loan owes this month. You may enter more — the ceiling is the loan&rsquo;s own remaining balance.
        </p>

        <div className="pw-table-scroll">
          <table className="pw-table pw-table--deductions pw-table--loans">
            <thead>
              <tr>
                <th className="th-employee">Employee</th>
                <th className="th-id">ID</th>
                <th>Loan</th>
                {monthKeys.map((k) => (
                  <th key={k} className="th-deduction">
                    {isCrossMonth ? formatMonthLabel(k) : 'This cycle'}
                    {isCrossMonth && <div className="month-header"><span>{k}</span></div>}
                  </th>
                ))}
                <th className="th-total">Remaining to deduct</th>
              </tr>
            </thead>
            <tbody>
              {displayedRows.map(({ emp, loans: mine }) =>
                mine.map((loan, i) => {
                  const entries = entriesOf(emp);
                  return (
                    <tr key={`${empKey(emp)}-${loan.id}`} className="employee-row">
                      {i === 0 && (
                        <>
                          <td className="pw-name-cell" rowSpan={mine.length}>
                            <div className="pw-name-cell__inner">
                              <div className="pw-avatar">{getInitials(emp)}</div>
                              <span className="pw-name-cell__text">{getFullName(emp)}</span>
                            </div>
                          </td>
                          <td rowSpan={mine.length}><span className="employee-id">{empKey(emp)}</span></td>
                        </>
                      )}
                      <td>
                        <div style={{ fontWeight: 600 }}>{loan.name}</div>
                      </td>
                      {monthKeys.map((k) => {
                        const payable = k >= loan.firstDeductionMonth;
                        const remaining = payable ? monthRemaining(loan, k) : 0;
                        const cap = payable ? capFor(loan, k, entries) : 0;
                        const value = amountOf(emp, loan, k);
                        const label = `${loan.name} — ${getFullName(emp)} — ${k}`;
                        return (
                          <td key={k} className="td-deduction">
                            <div className="deduction-inputs">
                              <div className="deduction-cell">
                                <input
                                  type="number"
                                  className="deduction-input"
                                  aria-label={label}
                                  value={value || ''}
                                  disabled={!payable}
                                  onChange={(e) => setAmount(emp, loan, k, e.target.value)}
                                  placeholder="0"
                                  min="0"
                                  step="0.01"
                                />
                                {payable ? (
                                  <button
                                    type="button"
                                    tabIndex={-1}
                                    // NOT struck through at zero, unlike Step 4's chip. Since the
                                    // input is capped at the LOAN's balance rather than the month's
                                    // (C2), "nothing due this month" is a live state in which the
                                    // runner may still collect — a strike-through read as "you
                                    // cannot" (/test-impl 2026-09-26).
                                    className="deduction-balance"
                                    title={`${peso(remaining)} of this month's ${peso(loan.monthlyAmortization)} is still due — click to fill`}
                                    aria-label={`Fill ${label}`}
                                    onClick={() => setAmount(emp, loan, k, Math.min(remaining, cap))}
                                  >
                                    {DUE_LABEL}: {peso(remaining)}
                                  </button>
                                ) : (
                                  <span className="deduction-disabled">Not yet payable</span>
                                )}
                              </div>
                            </div>
                          </td>
                        );
                      })}
                      <td className="td-total-gross">
                        <div style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{peso(loanRemaining(loan))}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--ui-text-muted)' }}>of {peso(loan.totalPayable)}</div>
                      </td>
                    </tr>
                  );
                }),
              )}
            </tbody>
          </table>
          {displayedRows.length === 0 && (
            <div className="deductions-no-results"><p>No employees found matching your criteria</p></div>
          )}
        </div>

        <div className="pw-footer">
          <div className="pw-footer__stats">
            <div className="pw-footer__stat">
              <span className="pw-footer__stat-label">Employees with loans</span>
              <span className="pw-footer__stat-value">{rows.length}</span>
            </div>
            <div className="pw-footer__stat">
              <span className="pw-footer__stat-label">Loans</span>
              <span className="pw-footer__stat-value">{rows.reduce((s, r) => s + r.loans.length, 0)}</span>
            </div>
            <div className="pw-footer__stat pw-footer__stat--total">
              <span className="pw-footer__stat-label">Total this run</span>
              <span className="pw-footer__stat-value">{peso(totalThisRun)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoansStep;
